/**
 * Wall-clock <-> instant conversion in the RESOLVED zone, for everything that
 * is not a pure display: <input type="datetime-local|date"> values, "today"
 * boundaries, and chart axis ticks.
 *
 * Why this exists: an <input type="datetime-local"> yields "2026-05-13T14:32"
 * with no zone. `new Date(str)` / `dayjs(str)` read that as BROWSER-local time,
 * so a user whose profile (or org) says Asia/Amman but whose laptop says UTC
 * picked 14:32 Amman and queried 14:32 UTC — three hours off, silently. Every
 * such input must round-trip through these two functions instead.
 */
import dayjs from './dayjsInit';
import { getCurrentTz, getCurrentTimeFormat } from './timezoneStore';
import { parseServerTs } from './parseServerTs';

const INPUT_DT = 'YYYY-MM-DDTHH:mm';
const INPUT_D = 'YYYY-MM-DD';

/** Epoch ms -> value for <input type="datetime-local"> in the resolved zone. */
export const toZonedInput = (value, tz = getCurrentTz()) => {
  const ms = parseServerTs(value);
  if (!Number.isFinite(ms)) return '';
  return dayjs(ms).tz(tz).format(INPUT_DT);
};

/** Epoch ms -> value for <input type="date"> in the resolved zone. */
export const toZonedDateInput = (value, tz = getCurrentTz()) => {
  const ms = parseServerTs(value);
  if (!Number.isFinite(ms)) return '';
  return dayjs(ms).tz(tz).format(INPUT_D);
};

/**
 * Wall-clock string ("2026-05-13T14:32", "2026-05-13 14:32", "2026-05-13")
 * read in the resolved zone -> epoch ms, or NaN when empty/unparseable.
 */
export const fromZonedInput = (str, tz = getCurrentTz()) => {
  if (!str || typeof str !== 'string') return NaN;
  const s = str.trim().replace(' ', 'T');
  if (!/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?)?$/.test(s)) return NaN;
  const d = dayjs.tz(s, tz);
  return d.isValid() ? d.valueOf() : NaN;
};

/** `fromZonedInput` as an ISO-8601 UTC string (for APIs that take ISO). */
export const fromZonedInputIso = (str, tz = getCurrentTz()) => {
  const ms = fromZonedInput(str, tz);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
};

/** "Now" as a dayjs in the resolved zone. */
export const zonedNow = (tz = getCurrentTz()) => dayjs().tz(tz);

/** Start of the calendar day containing `value`, in the resolved zone (epoch ms). */
export const startOfZonedDay = (value, tz = getCurrentTz()) => {
  const ms = value === undefined ? Date.now() : parseServerTs(value);
  if (!Number.isFinite(ms)) return NaN;
  return dayjs(ms).tz(tz).startOf('day').valueOf();
};

/** End (last ms) of the calendar day containing `value`, in the resolved zone. */
export const endOfZonedDay = (value, tz = getCurrentTz()) => {
  const ms = value === undefined ? Date.now() : parseServerTs(value);
  if (!Number.isFinite(ms)) return NaN;
  return dayjs(ms).tz(tz).endOf('day').valueOf();
};

/** "YYYY-MM-DD" calendar key of `value` in the resolved zone (grouping by day). */
export const zonedDayKey = (value, tz = getCurrentTz()) => {
  const ms = parseServerTs(value);
  if (!Number.isFinite(ms)) return '';
  return dayjs(ms).tz(tz).format(INPUT_D);
};

/** Hour of day (0-23) of `value` in the resolved zone. */
export const zonedHour = (value, tz = getCurrentTz()) => {
  const ms = parseServerTs(value);
  if (!Number.isFinite(ms)) return NaN;
  return dayjs(ms).tz(tz).hour();
};

/**
 * Chart axis tick in the resolved zone. Picks a granularity from the visible
 * span: time only within a day, date + time within a week, date beyond.
 */
export const formatAxisTick = (value, spanMs = 0, tz = getCurrentTz()) => {
  const ms = parseServerTs(value);
  if (!Number.isFinite(ms)) return '';
  const d = dayjs(ms).tz(tz);
  const t = getCurrentTimeFormat() === '12h' ? 'h:mm A' : 'HH:mm';
  if (spanMs > 7 * 86400000) return d.format('MMM D');
  if (spanMs > 86400000) return d.format(`MMM D ${t}`);
  return d.format(t);
};

const msOf = (value) => (
  typeof value === 'object' && value !== null && 'value' in value ? value.value : value
);

/**
 * ECharts `type: 'time'` axes label ticks in the BROWSER zone by default
 * (ECharts only knows local or UTC). Use this as `axisLabel.formatter` so
 * ticks read in the resolved zone: the date at a resolved-zone midnight, the
 * time otherwise.
 */
export const echartsTimeLabel = (value, tz = getCurrentTz()) => {
  const d = dayjs(Number(msOf(value))).tz(tz);
  if (!d.isValid()) return '';
  const t = getCurrentTimeFormat() === '12h' ? 'h:mm A' : 'HH:mm';
  return d.hour() === 0 && d.minute() === 0 ? d.format('MMM D') : d.format(t);
};

/** "MMM D, HH:mm:ss" in the resolved zone, for chart tooltips/axis pointers. */
export const echartsTooltipTime = (value, tz = getCurrentTz()) => {
  const d = dayjs(Number(msOf(value))).tz(tz);
  if (!d.isValid()) return '';
  return d.format(getCurrentTimeFormat() === '12h' ? 'MMM D, h:mm:ss A' : 'MMM D, HH:mm:ss');
};

/** Zone-correct extras for an ECharts time `xAxis`; spread into the axis config. */
export const echartsZonedTimeAxis = () => ({
  axisLabel: { formatter: (v) => echartsTimeLabel(v), hideOverlap: true },
  axisPointer: { label: { formatter: (p) => echartsTooltipTime(p) } },
});
